import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, GripVertical, Trash2, Pencil, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useTenant } from "@/contexts/TenantContext";
import { slugify } from "@/lib/utils";
import { FormFieldEditor, FormFieldWithOptions } from "@/components/forms/FormFieldEditor";
import { FormFieldOption } from "@/components/forms/HierarchicalOptionsEditor";
import { FormFieldDependency } from "@/components/forms/FieldDependencyEditor";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { logCrud } from "@/services/activities";

export type FormWithFields = {
  id?: string;
  nome: string;
  descricao?: string;
  slug: string;
  status: "ativo" | "inativo" | "rascunho";
  permite_multiplas_respostas: boolean;
  exige_autenticacao: boolean;
  disponivel_para_produtos?: boolean;
  disponivel_para_clientes?: boolean;
  disponivel_para_fornecedores?: boolean;
  disponivel_para_usuarios?: boolean;
  data_inicio?: string;
  data_fim?: string;
  fields?: FormFieldWithOptions[];
};

export default function FormEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();
  const isNew = !id;
  
  const [fields, setFields] = useState<FormFieldWithOptions[]>([]);
  const [editingFieldIndex, setEditingFieldIndex] = useState<number | null>(null);
  const [showFieldEditor, setShowFieldEditor] = useState(false);

  // Sensores para drag and drop (deve estar no topo do componente)
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Buscar formulário existente se estiver editando
  const { data: formData, isLoading } = useQuery({
    queryKey: ["form-with-fields", id],
    queryFn: async () => {
      if (!id) return null;

      // Buscar formulário
      const { data: form, error: formError } = await supabase
        .from("forms")
        .select("*")
        .eq("id", id)
        .single();

      if (formError) throw formError;

      // Buscar campos
      const { data: fieldsData, error: fieldsError } = await supabase
        .from("form_fields")
        .select("*")
        .eq("form_id", id)
        .order("ordem", { ascending: true });

      if (fieldsError) throw fieldsError;

      // Buscar opções para cada campo
      const fieldsWithOptions = await Promise.all(
        (fieldsData || []).map(async (field) => {
          const { data: optionsData } = await supabase
            .from("form_field_options")
            .select("*")
            .eq("field_id", field.id)
            .order("ordem", { ascending: true });

          // Organizar opções hierarquicamente
          const options = (optionsData || []) as any[];
          const rootOptions = options.filter((opt) => !opt.parent_option_id);
          const buildTree = (parentId: string | null): any[] => {
            return options
              .filter((opt) => opt.parent_option_id === parentId)
              .map((opt) => ({
                ...opt,
                sub_opcoes: buildTree(opt.id),
              }));
          };

          return {
            ...field,
            options: rootOptions.map((opt) => ({
              ...opt,
              sub_opcoes: buildTree(opt.id),
            })),
          };
        })
      );

      // Buscar dependências
      const fieldIds = fieldsWithOptions.map((f) => f.id);
      const { data: dependencies } = await supabase
        .from("form_field_dependencies")
        .select("*")
        .in("field_id", fieldIds.length > 0 ? fieldIds : [""]);

      // Buscar relacionamentos many-to-many
      // Primeiro, buscar todas as opções dos campos
      const allOptionIds = fieldsWithOptions.flatMap((f) => {
        const flatten = (opts: any[]): string[] => {
          const ids: string[] = [];
          for (const opt of opts) {
            ids.push(opt.id);
            if (opt.sub_opcoes && opt.sub_opcoes.length > 0) {
              ids.push(...flatten(opt.sub_opcoes));
            }
          }
          return ids;
        };
        return f.options ? flatten(f.options) : [];
      });

      let optionRelations: any[] = [];
      if (allOptionIds.length > 0) {
        console.log("[DEBUG] FormEdit - Buscando relacionamentos de opções ao carregar formulário", {
          allOptionIdsCount: allOptionIds.length,
          allOptionIds: allOptionIds.slice(0, 10), // Mostrar apenas os primeiros 10
        });

        const { data, error } = await supabase
          .from("form_field_option_relations")
          .select("*")
          .in("option_id", allOptionIds);
        
        if (error) {
          console.error("[ERROR] FormEdit - Erro ao buscar relacionamentos de opções", {
            allOptionIdsCount: allOptionIds.length,
            error: error,
            errorCode: error.code,
            errorMessage: error.message,
            errorDetails: error.details,
          });
        } else {
          console.log("[DEBUG] FormEdit - Relacionamentos encontrados ao carregar", {
            relationsCount: data?.length || 0,
            relations: data?.slice(0, 10), // Mostrar apenas os primeiros 10
          });
        }
        
        optionRelations = data || [];
      } else {
        console.log("[DEBUG] FormEdit - Nenhuma opção para buscar relacionamentos ao carregar");
      }

      // Associar dependências e relacionamentos aos campos
      const fieldsWithDependencies = fieldsWithOptions.map((field) => {
        const dependency = dependencies?.find((d) => d.field_id === field.id);
        
        if (dependency) {
          // Buscar relacionamentos deste campo
          const fieldOptionIds = (() => {
            const flatten = (opts: any[]): string[] => {
              const ids: string[] = [];
              for (const opt of opts) {
                ids.push(opt.id);
                if (opt.sub_opcoes && opt.sub_opcoes.length > 0) {
                  ids.push(...flatten(opt.sub_opcoes));
                }
              }
              return ids;
            };
            return field.options ? flatten(field.options) : [];
          })();

          const fieldRelations = optionRelations.filter((rel) =>
            fieldOptionIds.includes(rel.option_id)
          );

          console.log("[DEBUG] FormEdit - Associando relacionamentos ao campo", {
            fieldId: field.id,
            fieldNome: field.nome,
            fieldOptionIdsCount: fieldOptionIds.length,
            fieldOptionIds: fieldOptionIds.slice(0, 5), // Mostrar apenas os primeiros 5
            totalRelations: optionRelations.length,
            fieldRelationsCount: fieldRelations.length,
            fieldRelations: fieldRelations.slice(0, 5), // Mostrar apenas os primeiros 5
          });

          return {
            ...field,
            dependency: {
              depends_on_field_id: dependency.depends_on_field_id,
              option_relations: fieldRelations.map((rel) => ({
                option_id: rel.option_id,
                related_option_id: rel.related_option_id,
              })),
            },
          };
        }

        return field;
      });

      return {
        ...form,
        fields: fieldsWithDependencies,
      } as FormWithFields;
    },
    enabled: !!id,
  });

  // Verificar se o nome já existe (validação assíncrona)
  const checkNomeUnico = async (nome: string): Promise<boolean> => {
    if (!tenantId || !nome.trim()) return true;
    
    const { data, error } = await supabase
      .from("forms")
      .select("id")
      .eq("nome", nome.trim())
      .eq("tenant_id", tenantId)
      .maybeSingle();
    
    if (error) {
      console.error("[ERROR] Erro ao verificar nome único:", error);
      return true; // Em caso de erro, permitir para não bloquear
    }
    
    // Se está editando, permitir se for o mesmo formulário
    if (id && data?.id === id) {
      return true;
    }
    
    // Se não está editando ou é outro formulário, não permitir se já existe
    return !data;
  };

  // Schema com validação assíncrona de nome único
  const formSchema = z.object({
    nome: z
      .string()
      .min(1, "Nome é obrigatório")
      .refine(
        async (nome) => {
          return await checkNomeUnico(nome);
        },
        {
          message: "Já existe um formulário com este nome no seu tenant",
        }
      ),
    descricao: z.string().optional(),
    slug: z.string().min(1, "Slug é obrigatório"),
    status: z.enum(["ativo", "inativo", "rascunho"]).default("rascunho"),
    permite_multiplas_respostas: z.boolean().default(true),
    exige_autenticacao: z.boolean().default(false),
    disponivel_para_produtos: z.boolean().default(false),
    disponivel_para_clientes: z.boolean().default(false),
    disponivel_para_fornecedores: z.boolean().default(false),
    disponivel_para_usuarios: z.boolean().default(false),
    data_inicio: z.string().optional(),
    data_fim: z.string().optional(),
  });

  type FormData = z.infer<typeof formSchema>;

  const formForm = useForm<FormData>({
    resolver: zodResolver(formSchema),
    mode: "onChange", // Validar enquanto digita
    defaultValues: {
      nome: "",
      descricao: "",
      slug: "",
      status: "rascunho",
      permite_multiplas_respostas: true,
      exige_autenticacao: false,
      disponivel_para_produtos: false,
      disponivel_para_clientes: false,
      disponivel_para_fornecedores: false,
      disponivel_para_usuarios: false,
      data_inicio: "",
      data_fim: "",
    },
  });

  useEffect(() => {
    if (formData) {
      formForm.reset({
        nome: formData.nome,
        descricao: formData.descricao || "",
        slug: formData.slug,
        status: formData.status,
        permite_multiplas_respostas: formData.permite_multiplas_respostas ?? true,
        exige_autenticacao: formData.exige_autenticacao ?? false,
        disponivel_para_produtos: formData.disponivel_para_produtos ?? false,
        disponivel_para_clientes: formData.disponivel_para_clientes ?? false,
        disponivel_para_fornecedores: formData.disponivel_para_fornecedores ?? false,
        disponivel_para_usuarios: formData.disponivel_para_usuarios ?? false,
        data_inicio: formData.data_inicio || "",
        data_fim: formData.data_fim || "",
      });
      setFields(formData.fields || []);
    }
  }, [formData, formForm]);

  // Gerar slug automaticamente
  const handleNomeChange = (value: string) => {
    formForm.setValue("nome", value);
    // Sempre gerar slug automaticamente quando o nome muda
    const slug = slugify(value);
    formForm.setValue("slug", slug);
  };

  // Mutation para atualizar apenas a ordem dos campos
  const updateFieldsOrderMutation = useMutation({
    mutationFn: async (reorderedFields: FormFieldWithOptions[]) => {
      if (!id) return; // Se for formulário novo, não salvar ainda

      // Atualizar a ordem de cada campo no banco
      const updates = reorderedFields.map((field, index) => {
        if (field.id) {
          return supabase
            .from("form_fields")
            .update({ ordem: index })
            .eq("id", field.id);
        }
        return Promise.resolve({ data: null, error: null });
      });

      await Promise.all(updates);
    },
    onSuccess: () => {
      toast.success("Ordem dos campos atualizada");
      queryClient.invalidateQueries({ queryKey: ["form-with-fields", id] });
    },
    onError: (error: Error) => {
      toast.error("Erro ao atualizar ordem: " + error.message);
    },
  });

  // Handler para drag and drop
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = parseInt(active.id as string);
      const newIndex = parseInt(over.id as string);

      const reorderedFields = arrayMove(fields, oldIndex, newIndex);
      
      // Atualizar a ordem de cada campo
      const fieldsWithUpdatedOrder = reorderedFields.map((field, index) => ({
        ...field,
        ordem: index,
      }));

      // Atualizar estado local imediatamente para feedback visual
      setFields(fieldsWithUpdatedOrder);

      // Salvar no banco se o formulário já existir
      if (id) {
        updateFieldsOrderMutation.mutate(fieldsWithUpdatedOrder);
      }
    }
  };

  const createMutation = useMutation({
    mutationFn: async (data: FormData) => {
      if (!tenantId) throw new Error("Tenant ID não encontrado");

      // Criar formulário
      const { data: formData, error: formError } = await supabase
        .from("forms")
        .insert({
          nome: data.nome,
          descricao: data.descricao || null,
          slug: data.slug,
          status: data.status,
          permite_multiplas_respostas: data.permite_multiplas_respostas,
          exige_autenticacao: data.exige_autenticacao,
          disponivel_para_produtos: data.disponivel_para_produtos ?? false,
          disponivel_para_clientes: data.disponivel_para_clientes ?? false,
          disponivel_para_fornecedores: data.disponivel_para_fornecedores ?? false,
          disponivel_para_usuarios: data.disponivel_para_usuarios ?? false,
          data_inicio: data.data_inicio || null,
          data_fim: data.data_fim || null,
          tenant_id: tenantId,
        })
        .select()
        .single();

      if (formError) throw formError;

      // Criar campos
      if (fields.length > 0 && formData) {
        const fieldOptionIdMaps = await createFields(formData.id, fields);
        // Criar dependências e relacionamentos (com mapeamento de IDs)
        await createDependenciesAndRelations(formData.id, fields, fieldOptionIdMaps);
      }

      return formData;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["forms"] });
      queryClient.invalidateQueries({ queryKey: ["form-data"] }); // Invalidar cache do DynamicForm
      toast.success("Formulário criado com sucesso");
      if (data?.id) {
        void logCrud({ entitySlug: "entity:forms", operationSlug: "create", entityId: data.id, entityName: data.nome, screenSlug: "configuracoes/formularios" });
      }
      navigate(`/configuracoes/formularios/${data.id}`);
    },
    onError: (error: Error) => {
      toast.error("Erro ao criar formulário: " + error.message);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: FormData) => {
      if (!id) throw new Error("Form ID não encontrado");

      // Atualizar formulário
      const { error: formError } = await supabase
        .from("forms")
        .update({
          nome: data.nome,
          descricao: data.descricao || null,
          slug: data.slug,
          status: data.status,
          permite_multiplas_respostas: data.permite_multiplas_respostas,
          exige_autenticacao: data.exige_autenticacao,
          disponivel_para_produtos: data.disponivel_para_produtos ?? false,
          disponivel_para_clientes: data.disponivel_para_clientes ?? false,
          disponivel_para_fornecedores: data.disponivel_para_fornecedores ?? false,
          disponivel_para_usuarios: data.disponivel_para_usuarios ?? false,
          data_inicio: data.data_inicio || null,
          data_fim: data.data_fim || null,
        })
        .eq("id", id);

      if (formError) throw formError;

      // Sincronizar campos
      console.log("[DEBUG] FormEdit - Sincronizando campos");
      const fieldOptionIdMaps = await syncFields(id, fields);
      console.log("[DEBUG] FormEdit - Campos sincronizados, mapeamentos de IDs obtidos", {
        fieldOptionIdMapsSize: fieldOptionIdMaps.size,
        fieldOptionIdMapsEntries: Array.from(fieldOptionIdMaps.entries()).map(([fieldId, optionMap]) => ({
          fieldId,
          optionMapSize: optionMap.size,
          optionMapEntries: Array.from(optionMap.entries()).slice(0, 3), // Mostrar apenas os primeiros 3
        })),
      });

      // Sincronizar dependências e relacionamentos (com mapeamento de IDs)
      console.log("[DEBUG] FormEdit - Sincronizando dependências e relacionamentos");
      await syncDependenciesAndRelations(id, fields, fieldOptionIdMaps);
      console.log("[DEBUG] FormEdit - Dependências e relacionamentos sincronizados");

      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["forms"] });
      queryClient.invalidateQueries({ queryKey: ["form-with-fields", id] });
      queryClient.invalidateQueries({ queryKey: ["form-data"] }); // Invalidar cache do DynamicForm
      toast.success("Formulário atualizado com sucesso");
      if (id) {
        void logCrud({ entitySlug: "entity:forms", operationSlug: "update", entityId: id, screenSlug: "configuracoes/formularios", before: (formData as any), after: (formData as any) });
      }
    },
    onError: (error: Error) => {
      toast.error("Erro ao atualizar formulário: " + error.message);
    },
  });

  const createFields = async (formId: string, fieldsToCreate: FormFieldWithOptions[]) => {
    const fieldOptionIdMaps = new Map<string, Map<string, string>>(); // fieldId -> (tempId -> realId)

    for (let index = 0; index < fieldsToCreate.length; index++) {
      const field = fieldsToCreate[index];
      const { data: fieldData, error: fieldError } = await supabase
        .from("form_fields")
        .insert({
          form_id: formId,
          nome: field.nome,
          slug: field.slug,
          tipo: field.tipo,
          obrigatorio: field.obrigatorio,
          placeholder: field.placeholder || null,
          descricao: field.descricao || null,
          ordem: index, // Usar índice como ordem
          permite_sub_opcoes: field.permite_sub_opcoes,
          modifica_valor_produto: field.modifica_valor_produto || false,
          tenant_id: tenantId!,
        })
        .select()
        .single();

      if (fieldError) throw fieldError;

      // Criar opções se houver e obter mapeamento de IDs
      if (field.options && field.options.length > 0 && fieldData) {
        const optionIdMap = await createOptions(fieldData.id, field.options);
        fieldOptionIdMaps.set(fieldData.id, optionIdMap);
      }
    }

    return fieldOptionIdMaps;
  };

  const createOptions = async (fieldId: string, options: FormFieldOption[]): Promise<Map<string, string>> => {
    const optionIdMap = new Map<string, string>();

    const createOptionRecursive = async (
      opt: FormFieldOption,
      parentId: string | null = null
    ): Promise<string> => {
      const { sub_opcoes, id: tempId, ...optData } = opt;
      
      const { data: newOption, error } = await supabase
        .from("form_field_options")
        .insert({
          field_id: fieldId,
          valor: optData.valor,
          label: optData.label,
          ordem: optData.ordem,
          parent_option_id: parentId,
          valor_produto: optData.valor_produto || null,
          tenant_id: tenantId!,
        })
        .select()
        .single();

      if (error) throw error;

      const realId = newOption.id;
      if (tempId && tempId.startsWith("temp-")) {
        optionIdMap.set(tempId, realId);
      } else if (tempId) {
        // Se não é temporário, mapear para ele mesmo
        optionIdMap.set(tempId, realId);
      }

      // Criar sub-opções recursivamente
      if (sub_opcoes && sub_opcoes.length > 0) {
        for (const subOpt of sub_opcoes) {
          await createOptionRecursive(subOpt, realId);
        }
      }

      return realId;
    };

    // Criar todas as opções raiz
    for (const opt of options) {
      await createOptionRecursive(opt, null);
    }

    return optionIdMap;
  };

  const syncFields = async (formId: string, currentFields: FormFieldWithOptions[]) => {
    const fieldOptionIdMaps = new Map<string, Map<string, string>>(); // fieldId -> (tempId -> realId)

    // Buscar campos existentes
    const { data: existingFields } = await supabase
      .from("form_fields")
      .select("id")
      .eq("form_id", formId);

    const existingIds = new Set(existingFields?.map(f => f.id) || []);
    const currentIds = new Set(currentFields.filter(f => f.id).map(f => f.id!));

    // Remover campos que não estão mais na lista
    const toDelete = Array.from(existingIds).filter(id => !currentIds.has(id));
    if (toDelete.length > 0) {
      await supabase.from("form_fields").delete().in("id", toDelete);
    }

    // Atualizar/criar campos
    for (let index = 0; index < currentFields.length; index++) {
      const field = currentFields[index];
      if (field.id) {
        // Atualizar campo existente
        await supabase
          .from("form_fields")
          .update({
            nome: field.nome,
            slug: field.slug,
            tipo: field.tipo,
            obrigatorio: field.obrigatorio,
            placeholder: field.placeholder || null,
            descricao: field.descricao || null,
            ordem: index, // Usar índice como ordem
            permite_sub_opcoes: field.permite_sub_opcoes,
            modifica_valor_produto: field.modifica_valor_produto || false,
          })
          .eq("id", field.id);

        // Sincronizar opções do campo e obter mapeamento de IDs
        if (field.options) {
          const optionIdMap = await syncFieldOptions(field.id, field.options);
          fieldOptionIdMaps.set(field.id, optionIdMap);
        }
      } else {
        // Criar novo campo
        const { data: newField } = await supabase
          .from("form_fields")
          .insert({
            form_id: formId,
            nome: field.nome,
            slug: field.slug,
            tipo: field.tipo,
            obrigatorio: field.obrigatorio,
            placeholder: field.placeholder || null,
            descricao: field.descricao || null,
            ordem: index, // Usar índice como ordem
            permite_sub_opcoes: field.permite_sub_opcoes,
            modifica_valor_produto: field.modifica_valor_produto || false,
            tenant_id: tenantId!,
          })
          .select()
          .single();

        if (newField && field.options) {
          const optionIdMap = await createOptions(newField.id, field.options);
          fieldOptionIdMaps.set(newField.id, optionIdMap);
        }
      }
    }

    return fieldOptionIdMaps;
  };

  const syncFieldOptions = async (fieldId: string, options: FormFieldOption[]): Promise<Map<string, string>> => {
    console.log("[DEBUG] syncFieldOptions - Iniciando", {
      fieldId,
      optionsCount: options.length,
    });

    // Buscar opções existentes no banco
    const { data: existingOptions, error: fetchError } = await supabase
      .from("form_field_options")
      .select("*")
      .eq("field_id", fieldId);

    if (fetchError) {
      console.error("[ERROR] syncFieldOptions - Erro ao buscar opções existentes", {
        fieldId,
        error: fetchError,
        errorCode: fetchError.code,
        errorMessage: fetchError.message,
      });
      throw fetchError;
    }

    console.log("[DEBUG] syncFieldOptions - Opções existentes encontradas", {
      fieldId,
      existingOptionsCount: existingOptions?.length || 0,
    });

    const existingOptionsMap = new Map<string, { id: string; valor: string; label: string }>();
    (existingOptions || []).forEach((opt) => {
      existingOptionsMap.set(`${opt.valor}-${opt.label}`, opt);
    });

    const optionIdMap = new Map<string, string>();
    const optionsToDelete = new Set((existingOptions || []).map((opt) => opt.id));

    // Função recursiva para sincronizar opções
    const syncOptionRecursive = async (
      opt: FormFieldOption,
      parentId: string | null = null
    ): Promise<string> => {
      const key = `${opt.valor}-${opt.label}`;
      const existing = existingOptionsMap.get(key);

      if (existing && existing.id) {
        // Opção já existe, atualizar se necessário
        const { data: updatedOption, error: updateError } = await supabase
          .from("form_field_options")
          .update({
            ordem: opt.ordem,
            parent_option_id: parentId,
            valor_produto: opt.valor_produto || null,
          })
          .eq("id", existing.id)
          .select()
          .single();

        if (updateError) {
          console.error("[ERROR] syncFieldOptions - Erro ao atualizar opção existente", {
            fieldId,
            optionId: existing.id,
            valor: opt.valor,
            label: opt.label,
            error: updateError,
            errorCode: updateError.code,
            errorMessage: updateError.message,
          });
          throw updateError;
        }

        if (updatedOption) {
          optionsToDelete.delete(existing.id);
          if (opt.id && opt.id.startsWith("temp-")) {
            optionIdMap.set(opt.id, existing.id);
          } else if (opt.id) {
            optionIdMap.set(opt.id, existing.id);
          }
          console.log("[DEBUG] syncFieldOptions - Opção atualizada", {
            fieldId,
            originalId: opt.id,
            realId: existing.id,
            valor: opt.valor,
            label: opt.label,
          });
          return existing.id;
        }
      }

      // Criar nova opção
      const { data: newOption, error } = await supabase
        .from("form_field_options")
        .insert({
          field_id: fieldId,
          valor: opt.valor,
          label: opt.label,
          ordem: opt.ordem,
          parent_option_id: parentId,
          valor_produto: opt.valor_produto || null,
          tenant_id: tenantId!,
        })
        .select()
        .single();

      if (error) {
        console.error("[ERROR] syncFieldOptions - Erro ao criar nova opção", {
          fieldId,
          valor: opt.valor,
          label: opt.label,
          parentId,
          error: error,
          errorCode: error.code,
          errorMessage: error.message,
          errorDetails: error.details,
        });
        throw error;
      }

      const realId = newOption.id;
      if (opt.id && opt.id.startsWith("temp-")) {
        optionIdMap.set(opt.id, realId);
      } else if (opt.id) {
        optionIdMap.set(opt.id, realId);
      }

      return realId;
    };

    // Sincronizar todas as opções recursivamente
    const syncOptionsRecursive = async (
      opts: FormFieldOption[],
      parentId: string | null = null
    ) => {
      for (const opt of opts) {
        const { sub_opcoes, ...optData } = opt;
        const realId = await syncOptionRecursive(optData as FormFieldOption, parentId);
        if (sub_opcoes && sub_opcoes.length > 0) {
          await syncOptionsRecursive(sub_opcoes, realId);
        }
      }
    };

    if (options.length > 0) {
      await syncOptionsRecursive(options);
    }

    // Deletar opções que não existem mais
    if (optionsToDelete.size > 0) {
      console.log("[DEBUG] syncFieldOptions - Deletando opções que não existem mais", {
        fieldId,
        optionsToDeleteCount: optionsToDelete.size,
        optionsToDelete: Array.from(optionsToDelete).slice(0, 10), // Mostrar apenas os primeiros 10
      });

      const { error: deleteError } = await supabase
        .from("form_field_options")
        .delete()
        .in("id", Array.from(optionsToDelete));

      if (deleteError) {
        console.error("[ERROR] syncFieldOptions - Erro ao deletar opções", {
          fieldId,
          optionsToDeleteCount: optionsToDelete.size,
          error: deleteError,
          errorCode: deleteError.code,
          errorMessage: deleteError.message,
        });
      }
    }

    console.log("[DEBUG] syncFieldOptions - Concluído", {
      fieldId,
      optionIdMapSize: optionIdMap.size,
      optionIdMapEntries: Array.from(optionIdMap.entries()).slice(0, 10), // Mostrar apenas os primeiros 10
    });

    return optionIdMap;
  };

  // Criar dependências e relacionamentos
  const createDependenciesAndRelations = async (
    formId: string,
    fields: FormFieldWithOptions[],
    fieldOptionIdMaps?: Map<string, Map<string, string>>
  ) => {
    console.log("[DEBUG] createDependenciesAndRelations - Iniciando", {
      formId,
      fieldsCount: fields.length,
      hasFieldOptionIdMaps: !!fieldOptionIdMaps,
    });

    // Buscar todos os campos para mapear IDs de opções relacionadas também
    const allFieldsMap = new Map<string, FormFieldWithOptions>();
    fields.forEach((f) => {
      if (f.id) allFieldsMap.set(f.id, f);
    });

    console.log("[DEBUG] createDependenciesAndRelations - Campos mapeados", {
      allFieldsMapSize: allFieldsMap.size,
      fieldIds: Array.from(allFieldsMap.keys()),
    });

    for (const field of fields) {
      if (field.dependency && field.dependency.depends_on_field_id && field.id) {
        console.log("[DEBUG] createDependenciesAndRelations - Processando dependência", {
          fieldId: field.id,
          fieldNome: field.nome,
          dependsOnFieldId: field.dependency.depends_on_field_id,
          optionRelationsCount: field.dependency.option_relations.length,
          optionRelations: field.dependency.option_relations,
        });

        // Criar dependência
        const { error: depError } = await supabase
          .from("form_field_dependencies")
          .insert({
            field_id: field.id,
            depends_on_field_id: field.dependency.depends_on_field_id,
            condition_value: null,
            condition_operator: "equals",
            tenant_id: tenantId!,
          });

        if (depError && depError.code !== "23505") {
          // Ignorar erro de duplicata
          console.error("[ERROR] createDependenciesAndRelations - Erro ao criar dependência", {
            fieldId: field.id,
            fieldNome: field.nome,
            dependsOnFieldId: field.dependency.depends_on_field_id,
            error: depError,
            errorCode: depError.code,
            errorMessage: depError.message,
            errorDetails: depError.details,
          });
        } else if (depError && depError.code === "23505") {
          console.log("[DEBUG] createDependenciesAndRelations - Dependência já existe (duplicata ignorada)", {
            fieldId: field.id,
          });
        }

        // Criar relacionamentos entre opções
        if (field.dependency.option_relations.length > 0) {
          // Obter mapeamento de IDs para este campo
          const optionIdMap = fieldOptionIdMaps?.get(field.id) || new Map<string, string>();
          
          // Obter mapeamento de IDs para o campo relacionado (pai)
          const relatedField = allFieldsMap.get(field.dependency.depends_on_field_id);
          const relatedOptionIdMap = relatedField && fieldOptionIdMaps?.get(relatedField.id || "")
            ? fieldOptionIdMaps.get(relatedField.id || "")!
            : new Map<string, string>();

          console.log("[DEBUG] createDependenciesAndRelations - Mapeamentos de IDs", {
            fieldId: field.id,
            fieldNome: field.nome,
            optionIdMapSize: optionIdMap.size,
            optionIdMapEntries: Array.from(optionIdMap.entries()),
            relatedFieldId: relatedField?.id,
            relatedFieldNome: relatedField?.nome,
            relatedOptionIdMapSize: relatedOptionIdMap.size,
            relatedOptionIdMapEntries: Array.from(relatedOptionIdMap.entries()),
            originalOptionRelations: field.dependency.option_relations,
          });

          // Mapear IDs temporários para IDs reais
          console.log("[DEBUG] createDependenciesAndRelations - Mapeando relacionamentos", {
            fieldId: field.id,
            originalRelationsCount: field.dependency.option_relations.length,
            originalRelations: field.dependency.option_relations,
          });
          
          const relationsToCreate = field.dependency.option_relations
            .map((rel) => {
              // Mapear option_id (opção do campo atual) - opção do campo Modelo
              const realOptionId = optionIdMap.get(rel.option_id) || rel.option_id;
              // Mapear related_option_id (opção do campo pai) - opção do campo Escola
              const realRelatedOptionId = relatedOptionIdMap.get(rel.related_option_id) || rel.related_option_id;

              console.log("[DEBUG] createDependenciesAndRelations - Mapeando relacionamento individual", {
                originalOptionId: rel.option_id,
                originalRelatedOptionId: rel.related_option_id,
                realOptionId,
                realRelatedOptionId,
                optionIdInMap: optionIdMap.has(rel.option_id),
                relatedOptionIdInMap: relatedOptionIdMap.has(rel.related_option_id),
              });

              return {
                option_id: realOptionId,
                related_option_id: realRelatedOptionId,
                tenant_id: tenantId!,
                originalOptionId: rel.option_id,
                originalRelatedOptionId: rel.related_option_id,
              };
            })
            .filter((rel) => {
              // Filtrar relacionamentos inválidos (IDs que não foram mapeados corretamente)
              const isValid = rel.option_id && rel.related_option_id && 
                     !rel.option_id.startsWith("temp-") && 
                     !rel.related_option_id.startsWith("temp-");
              
              if (!isValid) {
                console.warn("[WARNING] createDependenciesAndRelations - Relacionamento inválido filtrado", {
                  option_id: rel.option_id,
                  related_option_id: rel.related_option_id,
                  originalOptionId: rel.originalOptionId,
                  originalRelatedOptionId: rel.originalRelatedOptionId,
                });
              }
              
              return isValid;
            });

          console.log("[DEBUG] createDependenciesAndRelations - Relacionamentos a criar", {
            totalOriginal: field.dependency.option_relations.length,
            totalValid: relationsToCreate.length,
            relationsToCreate: relationsToCreate.map(r => ({
              option_id: r.option_id,
              related_option_id: r.related_option_id,
            })),
          });

          // Inserir relacionamentos (ignorar duplicatas)
          let successCount = 0;
          let errorCount = 0;
          for (const relation of relationsToCreate) {
            const { error: relError } = await supabase
              .from("form_field_option_relations")
              .insert({
                option_id: relation.option_id,
                related_option_id: relation.related_option_id,
                tenant_id: relation.tenant_id,
              });

            if (relError && relError.code !== "23505") {
              // Ignorar erro de duplicata
              errorCount++;
              console.error("[ERROR] createDependenciesAndRelations - Erro ao criar relacionamento", {
                fieldId: field.id,
                fieldNome: field.nome,
                option_id: relation.option_id,
                related_option_id: relation.related_option_id,
                error: relError,
                errorCode: relError.code,
                errorMessage: relError.message,
                errorDetails: relError.details,
                errorHint: relError.hint,
              });
            } else if (relError && relError.code === "23505") {
              console.log("[DEBUG] createDependenciesAndRelations - Relacionamento já existe (duplicata ignorada)", {
                option_id: relation.option_id,
                related_option_id: relation.related_option_id,
              });
            } else {
              successCount++;
            }
          }

          console.log("[DEBUG] createDependenciesAndRelations - Resultado da criação de relacionamentos", {
            fieldId: field.id,
            total: relationsToCreate.length,
            success: successCount,
            errors: errorCount,
            duplicates: relationsToCreate.length - successCount - errorCount,
          });
        } else {
          console.log("[DEBUG] createDependenciesAndRelations - Campo sem relacionamentos de opções", {
            fieldId: field.id,
            fieldNome: field.nome,
          });
        }
      } else {
        console.log("[DEBUG] createDependenciesAndRelations - Campo sem dependência", {
          fieldId: field.id,
          fieldNome: field.nome,
          hasDependency: !!field.dependency,
          dependsOnFieldId: field.dependency?.depends_on_field_id,
        });
      }
    }

    console.log("[DEBUG] createDependenciesAndRelations - Concluído");
  };

  // Sincronizar dependências e relacionamentos
  const syncDependenciesAndRelations = async (
    formId: string,
    fields: FormFieldWithOptions[],
    fieldOptionIdMaps?: Map<string, Map<string, string>>
  ) => {
    console.log("[DEBUG] syncDependenciesAndRelations - Iniciando", {
      formId,
      fieldsCount: fields.length,
      hasFieldOptionIdMaps: !!fieldOptionIdMaps,
    });

    const fieldIds = fields.filter((f) => f.id).map((f) => f.id!);

    if (fieldIds.length === 0) {
      console.log("[DEBUG] syncDependenciesAndRelations - Nenhum campo com ID, pulando sincronização");
      return;
    }

    console.log("[DEBUG] syncDependenciesAndRelations - Removendo dependências e relacionamentos antigos", {
      fieldIds,
      fieldIdsCount: fieldIds.length,
    });

    // Remover todas as dependências e relacionamentos dos campos existentes
    const { error: depDeleteError } = await supabase
      .from("form_field_dependencies")
      .delete()
      .in("field_id", fieldIds);

    if (depDeleteError) {
      console.error("[ERROR] syncDependenciesAndRelations - Erro ao remover dependências", {
        fieldIds,
        error: depDeleteError,
        errorCode: depDeleteError.code,
        errorMessage: depDeleteError.message,
      });
    }

    // Remover relacionamentos de opções dos campos
    const { data: allOptions, error: optionsError } = await supabase
      .from("form_field_options")
      .select("id")
      .in("field_id", fieldIds);

    if (optionsError) {
      console.error("[ERROR] syncDependenciesAndRelations - Erro ao buscar opções para remover relacionamentos", {
        fieldIds,
        error: optionsError,
        errorCode: optionsError.code,
        errorMessage: optionsError.message,
      });
    }

    const optionIds = allOptions?.map((opt) => opt.id) || [];

    console.log("[DEBUG] syncDependenciesAndRelations - Opções encontradas para remover relacionamentos", {
      optionIdsCount: optionIds.length,
      optionIds: optionIds.slice(0, 10), // Mostrar apenas os primeiros 10
    });

    if (optionIds.length > 0) {
      const { error: relDeleteError } = await supabase
        .from("form_field_option_relations")
        .delete()
        .in("option_id", optionIds);

      if (relDeleteError) {
        console.error("[ERROR] syncDependenciesAndRelations - Erro ao remover relacionamentos de opções", {
          optionIdsCount: optionIds.length,
          error: relDeleteError,
          errorCode: relDeleteError.code,
          errorMessage: relDeleteError.message,
        });
      } else {
        console.log("[DEBUG] syncDependenciesAndRelations - Relacionamentos antigos removidos com sucesso");
      }
    }

    // Criar novas dependências e relacionamentos (com mapeamento de IDs)
    console.log("[DEBUG] syncDependenciesAndRelations - Criando novas dependências e relacionamentos");
    await createDependenciesAndRelations(formId, fields, fieldOptionIdMaps);
    console.log("[DEBUG] syncDependenciesAndRelations - Concluído");
  };

  const handleAddField = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    console.log("[DEBUG] handleAddField - Antes de atualizar estado", {
      showFieldEditor,
      editingFieldIndex,
    });
    setEditingFieldIndex(null);
    setShowFieldEditor(true);
    console.log("[DEBUG] handleAddField - Depois de atualizar estado", {
      showFieldEditor: true,
      editingFieldIndex: null,
    });
  };

  const handleEditField = (index: number) => {
    setEditingFieldIndex(index);
    setShowFieldEditor(true);
  };

  const handleDisableOtherFields = () => {
    const updated = fields.map((f, index) => {
      // Se não é o campo sendo editado e tem modifica_valor_produto ativo, desativar
      // Se editingFieldIndex é null (campo novo), desativar todos os outros
      if ((editingFieldIndex !== null && index !== editingFieldIndex) || 
          (editingFieldIndex === null)) {
        if (f.modifica_valor_produto) {
          return { ...f, modifica_valor_produto: false };
        }
      }
      return f;
    });
    setFields(updated);
  };

  const handleSaveField = (fieldData: FormFieldWithOptions) => {
    console.log("[DEBUG] FormEdit - handleSaveField chamado", {
      editingFieldIndex,
      fieldNome: fieldData.nome,
      fieldId: fieldData.id,
      hasDependency: !!fieldData.dependency,
      dependency: fieldData.dependency,
      dependencyDependsOnFieldId: fieldData.dependency?.depends_on_field_id,
      dependencyOptionRelationsCount: fieldData.dependency?.option_relations?.length || 0,
      dependencyOptionRelations: fieldData.dependency?.option_relations,
      optionsCount: fieldData.options?.length || 0,
    });

    // Se está ativando modifica_valor_produto, desativar nos outros campos
    if (fieldData.modifica_valor_produto) {
      handleDisableOtherFields();
    }

    if (editingFieldIndex !== null) {
      // Atualizar campo existente
      const updated = [...fields];
      updated[editingFieldIndex] = { ...fieldData, id: fields[editingFieldIndex].id };
      setFields(updated);
      console.log("[DEBUG] FormEdit - Campo atualizado no estado", {
        index: editingFieldIndex,
        updatedField: updated[editingFieldIndex],
        hasDependency: !!updated[editingFieldIndex].dependency,
        dependencyOptionRelationsCount: updated[editingFieldIndex].dependency?.option_relations?.length || 0,
      });
    } else {
      // Adicionar novo campo
      setFields([...fields, fieldData]);
      console.log("[DEBUG] FormEdit - Campo adicionado ao estado", {
        newField: fieldData,
        hasDependency: !!fieldData.dependency,
        dependencyOptionRelationsCount: fieldData.dependency?.option_relations?.length || 0,
      });
    }
    setShowFieldEditor(false);
    setEditingFieldIndex(null);
  };

  const handleDeleteField = (index: number) => {
    const updated = fields.filter((_, i) => i !== index);
    setFields(updated);
    setShowFieldEditor(false);
    setEditingFieldIndex(null);
  };

  const onSubmit = (data: FormData) => {
    console.log("[DEBUG] FormEdit - onSubmit chamado", {
      isNew,
      fieldsCount: fields.length,
      fields: fields.map(f => ({
        id: f.id,
        nome: f.nome,
        tipo: f.tipo,
        hasDependency: !!f.dependency,
        dependencyDependsOnFieldId: f.dependency?.depends_on_field_id,
        dependencyOptionRelationsCount: f.dependency?.option_relations?.length || 0,
        dependencyOptionRelations: f.dependency?.option_relations,
        optionsCount: f.options?.length || 0,
      })),
    });

    if (isNew) {
      createMutation.mutate(data);
    } else {
      updateMutation.mutate(data);
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate("/configuracoes/formularios")}> 
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>
        <div>
          <h1 className="text-3xl font-bold">
            {isNew ? "Novo Formulário" : "Editar Formulário"}
          </h1>
          <p className="text-muted-foreground">
            {isNew
              ? "Crie um novo formulário customizado"
              : "Edite as informações do formulário"}
          </p>
        </div>
      </div>

      {!showFieldEditor ? (
        <Form {...formForm}>
          <form 
            onSubmit={(e) => {
              e.preventDefault();
              formForm.handleSubmit(onSubmit)(e);
            }} 
            className="space-y-6"
          >
            <Card>
              <CardHeader>
                <CardTitle>Informações Básicas</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Nome do Formulário e Status na mesma linha */}
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={formForm.control}
                    name="nome"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nome do Formulário</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            onChange={(e) => handleNomeChange(e.target.value)}
                            placeholder="Ex: Cadastro de Uniforme"
                          />
                        </FormControl>
                        {/* Identificador abaixo do nome, cinza, pequeno, não editável */}
                        <div className="text-xs text-muted-foreground mt-1">
                          Identificador: {formForm.watch("slug") || "será gerado automaticamente"}
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={formForm.control}
                    name="status"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Status</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione o status" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="rascunho">Rascunho</SelectItem>
                            <SelectItem value="ativo">Ativo</SelectItem>
                            <SelectItem value="inativo">Inativo</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Descrição como input de uma linha */}
                <FormField
                  control={formForm.control}
                  name="descricao"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Descrição</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Descrição do formulário" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Permitir múltiplas respostas e Exigir autenticação na mesma linha */}
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={formForm.control}
                    name="permite_multiplas_respostas"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                        <div className="space-y-0.5">
                          <FormLabel>Permitir múltiplas respostas</FormLabel>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={formForm.control}
                    name="exige_autenticacao"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                        <div className="space-y-0.5">
                          <FormLabel>Exigir autenticação</FormLabel>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>

                {/* Disponibilidade para entidades */}
                <div className="space-y-2">
                  <FormLabel className="text-base">Disponível para</FormLabel>
                  <FormDescription className="mb-4">
                    Selecione em quais entidades este formulário estará disponível
                  </FormDescription>
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={formForm.control}
                      name="disponivel_para_produtos"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center space-x-3 space-y-0 rounded-md border p-4">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel className="cursor-pointer">Produtos</FormLabel>
                            <FormDescription>
                              Formulário estará disponível para produtos
                            </FormDescription>
                          </div>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={formForm.control}
                      name="disponivel_para_clientes"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center space-x-3 space-y-0 rounded-md border p-4">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel className="cursor-pointer">Clientes</FormLabel>
                            <FormDescription>
                              Formulário estará disponível para clientes
                            </FormDescription>
                          </div>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={formForm.control}
                      name="disponivel_para_fornecedores"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center space-x-3 space-y-0 rounded-md border p-4">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel className="cursor-pointer">Fornecedores</FormLabel>
                            <FormDescription>
                              Formulário estará disponível para fornecedores
                            </FormDescription>
                          </div>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={formForm.control}
                      name="disponivel_para_usuarios"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center space-x-3 space-y-0 rounded-md border p-4">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel className="cursor-pointer">Usuários</FormLabel>
                            <FormDescription>
                              Formulário estará disponível para usuários
                            </FormDescription>
                          </div>
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Lista de campos */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Campos do Formulário</CardTitle>
                  <Button 
                    type="button" 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleAddField(e);
                    }}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Adicionar Campo
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {fields.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Nenhum campo cadastrado. Clique em "Adicionar Campo" para começar.
                  </p>
                ) : (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={fields.map((_, index) => index.toString())}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-2">
                        {fields.map((field, index) => (
                          <SortableFieldItem
                            key={field.id || `temp-${index}`}
                            field={field}
                            index={index}
                            onEdit={() => handleEditField(index)}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                )}
              </CardContent>
            </Card>

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/configuracoes/formularios")}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                {isNew ? "Criar" : "Salvar"} Formulário
              </Button>
            </div>
          </form>
        </Form>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>
              {editingFieldIndex !== null ? "Editar Campo" : "Novo Campo"}
            </CardTitle>
          </CardHeader>
          <CardContent>
          <FormFieldEditor
            field={editingFieldIndex !== null ? fields[editingFieldIndex] : undefined}
            allFields={fields.map((f) => ({
              id: f.id || `temp-${Date.now()}`,
              nome: f.nome,
              slug: f.slug,
              tipo: f.tipo,
              options: f.options,
              modifica_valor_produto: f.modifica_valor_produto,
            }))}
            onSave={handleSaveField}
            onCancel={() => {
              console.log("[DEBUG] FormFieldEditor - onCancel chamado");
              setShowFieldEditor(false);
              setEditingFieldIndex(null);
            }}
            onDelete={
              editingFieldIndex !== null
                ? () => handleDeleteField(editingFieldIndex)
                : undefined
            }
            onDisableOtherFields={handleDisableOtherFields}
          />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Componente para item arrastável
interface SortableFieldItemProps {
  field: FormFieldWithOptions;
  index: number;
  onEdit: () => void;
}

function SortableFieldItem({ field, index, onEdit }: SortableFieldItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: index.toString() });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Card>
        <CardContent className="p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div
                {...attributes}
                {...listeners}
                className="cursor-grab active:cursor-grabbing"
              >
                <GripVertical className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <div className="font-medium">{field.nome}</div>
                <div className="text-sm text-muted-foreground">
                  {field.tipo} {field.obrigatorio && "(obrigatório)"}
                </div>
              </div>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onEdit}
            >
              <Pencil className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

